import { useEffect, useRef, useCallback } from 'react';

const VERSION_CHECK_INTERVAL = 300000; // Check every 5 minutes
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to detect new app deployments.
 * 
 * Works in concert with the Service Worker:
 * - SW handles cache invalidation and page reload on update
 * - This hook triggers SW update checks on visibility change / interval
 * - Also acts as a fallback if SW is not supported
 * 
 * For iOS PWAs: The SW registration code handles update checks on
 * visibilitychange/focus. This hook is a belt-and-suspenders backup.
 */
export const useVersionCheck = () => {
  const currentVersion = useRef(null);
  const isChecking = useRef(false);
  const hasRefreshed = useRef(false);

  const triggerSWUpdate = useCallback(() => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg) reg.update().catch(() => {});
      });
    }
  }, []);

  const checkVersion = useCallback(async () => {
    if (isChecking.current || hasRefreshed.current) return;
    isChecking.current = true;

    try {
      const response = await fetch(`${API_URL}/api/version`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });

      if (!response.ok) {
        isChecking.current = false;
        return;
      }

      const data = await response.json();
      const newVersion = data.version;

      if (currentVersion.current === null) {
        currentVersion.current = newVersion;
        sessionStorage.setItem('app_version', newVersion);
      } else if (currentVersion.current !== newVersion) {
        // Version changed — trigger SW update check
        hasRefreshed.current = true;
        triggerSWUpdate();

        // If no SW is active (browser mode), do a hard reload
        if (!navigator.serviceWorker?.controller) {
          setTimeout(() => window.location.reload(), 1000);
        }
        // If SW is active, the SW update lifecycle will handle the reload
      }
    } catch (error) {
      // Silently fail
    } finally {
      isChecking.current = false;
    }
  }, [triggerSWUpdate]);

  useEffect(() => {
    const storedVersion = sessionStorage.getItem('app_version');
    if (storedVersion) {
      currentVersion.current = storedVersion;
    }

    // Delay initial check
    const initialTimeout = setTimeout(checkVersion, 10000);
    const intervalId = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    // Check when tab becomes visible
    let visTimeout = null;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(visTimeout);
        visTimeout = setTimeout(() => {
          checkVersion();
          triggerSWUpdate();
        }, 3000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(visTimeout);
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [checkVersion, triggerSWUpdate]);

  return { checkVersion };
};

export default useVersionCheck;
